import { call, put, select } from 'redux-saga/effects';
import { captureException } from '@sentry/nextjs';
import getMatchReasonsQuery from './getMatchReasonsQuery';
import getNonPiiAnswers from '../../../../Helpers/getNonPiiQuestionTexts';
import { exposeBrandMatchReasonsEvent } from '../../../../Helpers/events';
import selectIsPolarisFeatureEnabled from '../../../../Helpers/polaris';

/**
 * Fetches AI-generated match reasons for the given brand matches.
 * Only sends non-PII answers to the Gateway API.
 * Stores the results in Redux and exposes an event to the formLoader/results page.
 *
 * @param {Array} customerMatches - The customer brand matches from the customer match API
 */
export function* getMatchReasonsWorker(customerMatches) {
  const { components, optionalFormConfig, features, formConfig } =
    yield select();
  const { iFrameId } = optionalFormConfig;

  if (
    !selectIsPolarisFeatureEnabled(
      { features, formConfig },
      'enableResultsPage'
    )
  ) {
    return;
  }

  const matchedBrands = customerMatches
    .filter((match) => match.brandId)
    .map((match) => ({
      id: match.brandId,
      name: match.brandLabel,
    }));

  if (matchedBrands.length === 0) {
    return;
  }

  const answers = getNonPiiAnswers(components);

  if (Object.keys(answers).length === 0) {
    return;
  }

  if (!formConfig?.subcategoryId) {
    return;
  }

  try {
    const args = {
      variables: {
        input: {
          subcategory: {
            id: formConfig.subcategoryId,
            name: formConfig.subcategoryName,
          },
          countryCode: formConfig.countryCode,
          matchedBrands,
          answers,
        },
      },
    };

    const result = yield call(getMatchReasonsQuery, args);

    const matchReasonsMap = result?.matchReasons || {};
    const matchScoresMap = result?.matchScores || {};

    const brandMatchReasons = matchedBrands.map(({ id: brandId }) => {
      const reasons = matchReasonsMap[brandId];
      const rawScore = matchScoresMap[brandId];
      const matchScore =
        typeof rawScore === 'number' && Number.isFinite(rawScore)
          ? rawScore
          : null;

      return {
        brandId,
        reasons: reasons || '',
        generationType:
          reasons && reasons.length > 0 ? 'AI' : 'FALLBACK_WHAT_OTHERS_LIKE',
        matchScore,
      };
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setBrandMatchReasons',
      payload: {
        brandMatchReasons,
      },
    });

    exposeBrandMatchReasonsEvent({
      iFrameId,
      brandMatchReasons,
    });
  } catch (error) {
    // The results page will fall back to whatOthersLike content.
    captureException(error);
  }
}

export default getMatchReasonsWorker;
